import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { Sparkles, Upload, AlertTriangle, Check, X, Loader2, RefreshCw } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LabelList } from "recharts";
import { api, formatApiError, fmtInt, fmtCurrency } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

// -----------------------------------------------------------------------------
// AI PDF Extract Dialog
// -----------------------------------------------------------------------------
// Two-phase flow:
//   1) User picks a PDF → POST /extract-pdf → renders preview + subpost mapping
//   2) User confirms → POST /import-extracted → historicals upserted per year
//
// Recurrent-import handling: for years already present in DB the preview shows
// a warning + "Overschrijven" toggle (default: overwrite ON for extracted years).
// -----------------------------------------------------------------------------

// Slug-normalize for fuzzy match with existing subposts (case + accent insensitive).
function norm(s) {
  return (s || "").toString().toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "");
}

function suggestExisting(name, existing) {
  const n = norm(name);
  if (!n) return null;
  const hit = existing.find((e) => norm(e) === n);
  if (hit) return hit;
  const partial = existing.find((e) => norm(e).includes(n) || n.includes(norm(e)));
  return partial || null;
}

function initialMapping(subposts, existingList) {
  return (subposts || []).map((sp) => {
    const suggestion = suggestExisting(sp.name, existingList);
    return {
      original_name: sp.name,
      amount: Number(sp.amount) || 0,
      action: suggestion ? "existing" : "new",
      target_name: suggestion || sp.name,
    };
  });
}

function initialReservesMapping(items, existingReserves) {
  return (items || []).map((rm) => {
    // Suggest a match against existing reserves (case-insensitive, exact name).
    const nameLower = String(rm.name || "").trim().toLowerCase();
    const match = (existingReserves || []).find((r) => (r.name || "").toLowerCase() === nameLower);
    return {
      name: rm.name || "",
      category: rm.category || "earmarked",
      opening_balance: Number(rm.opening_balance) || 0,
      dotatie: Number(rm.dotatie) || 0,
      onttrekking: Number(rm.onttrekking) || 0,
      closing_balance: Number(rm.closing_balance) || 0,
      action: match ? "existing" : "new",
      target_name: match ? match.name : (rm.name || ""),
    };
  });
}

const KASSTROOM_SOURCE_LABEL = {
  kasstroomoverzicht: "Kasstroom: uit kasstroomoverzicht",
  balans: "Kasstroom: afgeleid uit de balans",
  geen: "Kasstroom: niet gevonden (handmatig invullen)",
};

function initialYearsState(preview) {
  return (preview?.years || []).map((y) => ({
    year: y.year,
    revenue: Number(y.revenue) || 0,
    operating_costs: Number(y.operating_costs) || 0,
    depreciation: Number(y.depreciation) || 0,
    interest_paid: Number(y.interest_paid) || 0,
    interest_income: Number(y.interest_income) || 0,
    cash_bank_balance: Number(y.cash_bank_balance) || 0,
    kasstroom: y.kasstroom || null,
    kasstroom_bron: y.kasstroom_bron || "geen",
    already_exists: Boolean(y.already_exists),
    overwrite: Boolean(y.already_exists), // default ON when it exists (user can toggle off)
    revenue_subposts: initialMapping(y.revenue_subposts, preview?.existing_subposts?.revenue || []),
    opex_subposts: initialMapping(y.opex_subposts, preview?.existing_subposts?.opex || []),
    reserves: initialReservesMapping(y.reserves, preview?.existing_reserves || []),
  }));
}

export default function AiPdfExtractDialog({ t, clubId, open, onOpenChange, onImported, locked = false, initialPreview = null }) {
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState("pick"); // pick | loading | review | saving
  const [preview, setPreview] = useState(null);
  const [years, setYears] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      // Reset on close.
      setFile(null); setPhase("pick"); setPreview(null); setYears([]); setError("");
      return;
    }
    // If a caller provides a pre-extracted preview (e.g. DemoSampleDialog),
    // skip the upload step and jump directly into review.
    if (initialPreview) {
      setPreview(initialPreview);
      setYears(initialYearsState(initialPreview));
      setPhase("review");
    }
  }, [open, initialPreview]);

  const existingSubposts = preview?.existing_subposts || { revenue: [], opex: [] };

  const runExtract = async () => {
    if (!file) return;
    setPhase("loading"); setError("");
    const form = new FormData(); form.append("file", file);
    // The Gemini 3.1 Pro extraction may take 22-70s. The Kubernetes ingress
    // proxy sometimes returns HTML 502 for calls >60s even though the backend
    // returns 200 shortly after. We retry once transparently on 5xx / no-JSON.
    const attempt = async () => {
      const { data } = await api.post(`/clubs/${clubId}/extract-pdf`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      return data;
    };
    const isTimeoutLike = (e) => {
      const status = e.response?.status;
      const isNetwork = !e.response;
      return isNetwork || status === 502 || status === 503 || status === 504 || status === 408;
    };
    let data = null;
    try {
      data = await attempt();
    } catch (e1) {
      if (isTimeoutLike(e1)) {
        try { data = await attempt(); }
        catch (e2) {
          setPhase("pick");
          setError(t("AI-analyse duurde te lang of gateway gaf een fout. Probeer het over een minuut opnieuw."));
          toast.error(t("AI-analyse duurde te lang. Probeer opnieuw."));
          return;
        }
      } else {
        setPhase("pick");
        const msg = formatApiError(e1.response?.data?.detail) || e1.message || t("AI-extractie mislukt.");
        setError(msg);
        toast.error(msg);
        return;
      }
    }
    setPreview(data);
    setYears(initialYearsState(data));
    setPhase("review");
    if (!(data.years || []).length) {
      setError(t("Geen boekjaren gevonden in het jaarverslag."));
    } else {
      toast.success(t("Extractie geslaagd — controleer & bevestig."));
    }
  };

  const runImport = async () => {
    setPhase("saving"); setError("");
    // Filter out years where overwrite=false AND already_exists (they are read-only skips).
    const payloadYears = years.filter((y) => !(y.already_exists && !y.overwrite)).map((y) => ({
      year: y.year,
      revenue: y.revenue,
      operating_costs: y.operating_costs,
      depreciation: y.depreciation,
      interest_paid: y.interest_paid,
      interest_income: y.interest_income,
      cash_bank_balance: y.cash_bank_balance,
      kasstroom: y.kasstroom_bron === "geen" ? null : y.kasstroom,
      revenue_subposts: y.revenue_subposts,
      opex_subposts: y.opex_subposts,
      reserves: y.reserves,
      overwrite: y.overwrite,
    }));
    if (!payloadYears.length) {
      setPhase("review");
      setError(t("Selecteer minstens één boekjaar om te importeren."));
      return;
    }
    try {
      const { data } = await api.post(`/clubs/${clubId}/import-extracted`, { years: payloadYears });
      toast.success(t("{n} boekjaren geïmporteerd ({u} bijgewerkt)").replace("{n}", data.inserted).replace("{u}", data.updated));
      onImported?.();
      onOpenChange?.(false);
    } catch (e) {
      setPhase("review");
      setError(formatApiError(e.response?.data?.detail) || t("Import mislukt."));
      toast.error(formatApiError(e.response?.data?.detail) || t("Import mislukt."));
    }
  };

  const patchYear = (yIdx, patch) => setYears((prev) => prev.map((y, i) => (i === yIdx ? { ...y, ...patch } : y)));
  const patchSubpost = (yIdx, cat, spIdx, patch) => setYears((prev) => prev.map((y, i) => {
    if (i !== yIdx) return y;
    const key = cat === "revenue" ? "revenue_subposts" : "opex_subposts";
    const list = y[key].slice();
    list[spIdx] = { ...list[spIdx], ...patch };
    return { ...y, [key]: list };
  }));
  const patchReserve = (yIdx, rIdx, patch) => setYears((prev) => prev.map((y, i) => {
    if (i !== yIdx) return y;
    const list = (y.reserves || []).slice();
    list[rIdx] = { ...list[rIdx], ...patch };
    return { ...y, reserves: list };
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="ai-pdf-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl uppercase tracking-tight flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" /> {initialPreview ? t("AI-import demo-jaarverslag") : t("AI-import jaarverslag (PDF)")}
          </DialogTitle>
          <DialogDescription>
            {initialPreview
              ? t("De AI heeft het demo-jaarverslag geanalyseerd. Controleer de voorgestelde historische data + subposten en bevestig om te importeren.")
              : t("Upload het jaarverslag (PDF). De AI leest de exploitatierekening en balans en stelt de historische data + subposten voor. Je controleert en bevestigt voordat er iets wordt opgeslagen.")}
          </DialogDescription>
        </DialogHeader>

        {locked && (
          <div className="p-3 bg-destructive/10 border-2 border-destructive text-destructive font-display uppercase text-sm font-bold flex items-center gap-2" data-testid="ai-pdf-locked-notice">
            <AlertTriangle className="w-4 h-4" /> {t("Historische data is vergrendeld. Ontgrendel eerst om te importeren.")}
          </div>
        )}

        {phase === "pick" && (
          <PickPane t={t} file={file} setFile={setFile} runExtract={runExtract} error={error} disabled={locked} />
        )}
        {phase === "loading" && <LoadingPane t={t} />}
        {phase === "saving" && <LoadingPane t={t} label={t("Opslaan…")} />}
        {phase === "review" && (
          <ReviewPane t={t} years={years} preview={preview} existingSubposts={existingSubposts}
                      patchYear={patchYear} patchSubpost={patchSubpost}
                      patchReserve={patchReserve}
                      onSave={runImport} onBack={() => { setPhase("pick"); setError(""); }} error={error} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PickPane({ t, file, setFile, runExtract, error, disabled }) {
  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-foreground p-8 text-center bg-muted/30">
        <Upload className="w-12 h-12 mx-auto mb-3 opacity-60" />
        <p className="font-display uppercase font-bold text-sm mb-3">{t("Kies een PDF-bestand (jaarverslag)")}</p>
        <input type="file" accept="application/pdf,.pdf" data-testid="ai-pdf-file-input"
               onChange={(e) => setFile(e.target.files?.[0] || null)}
               className="block mx-auto text-sm" disabled={disabled} />
        {file && (
          <p className="mt-3 text-xs font-mono-num" data-testid="ai-pdf-filename">
            {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
          </p>
        )}
      </div>
      {error && (
        <div className="p-3 bg-destructive/10 border-2 border-destructive text-destructive text-sm" data-testid="ai-pdf-error">{error}</div>
      )}
      <div className="text-xs text-muted-foreground space-y-1">
        <p>{t("Tips voor de beste extractie:")}</p>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>{t("Volledig jaarverslag met exploitatierekening én balans.")}</li>
          <li>{t("Bevat typisch 2 boekjaren (huidig + vorig — beide worden geïmporteerd).")}</li>
          <li>{t("Volgend jaar kun je gewoon het nieuwe jaarverslag uploaden — bestaande jaren worden slim bijgewerkt.")}</li>
        </ul>
      </div>
      <DialogFooter>
        <button onClick={runExtract} disabled={!file || disabled} data-testid="ai-pdf-extract-btn"
                className={`btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 ${(!file || disabled) ? "opacity-40 cursor-not-allowed" : ""}`}>
          <Sparkles className="w-4 h-4" /> {t("Analyseer met AI")}
        </button>
      </DialogFooter>
    </div>
  );
}

function LoadingPane({ t, label }) {
  return (
    <div className="py-10 flex flex-col items-center gap-3" data-testid="ai-pdf-loading">
      <Loader2 className="w-10 h-10 animate-spin text-primary" />
      <p className="font-display uppercase font-bold text-sm">{label || t("AI leest jaarverslag…")}</p>
      <p className="text-xs text-muted-foreground max-w-md text-center">{t("Dit kan 30-90 seconden duren afhankelijk van de omvang van het document. Bij een tijdelijke gateway-fout proberen we het automatisch nog een keer.")}</p>
    </div>
  );
}

function NumberCell({ value, onChange, testid }) {
  const [raw, setRaw] = useState(String(Math.round(Number(value) || 0)));
  useEffect(() => { setRaw(String(Math.round(Number(value) || 0))); }, [value]);
  return (
    <input
      type="text" inputMode="numeric"
      value={raw}
      onChange={(e) => setRaw(e.target.value.replace(/[^\d-]/g, ""))}
      onBlur={() => {
        const n = parseInt(raw, 10);
        const v = Number.isFinite(n) ? n : 0;
        onChange(v);
        setRaw(String(v));
      }}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      data-testid={testid}
      className="w-full text-right font-mono-num text-sm px-2 py-1 border border-input bg-background"
    />
  );
}

function ExtractPreviewChart({ t, years }) {
  const data = useMemo(() => (years || []).map((y) => {
    const inkomsten = Number(y.revenue) || 0;
    const kosten = Number(y.operating_costs) || 0;
    const afschrijving = Number(y.depreciation) || 0;
    const rentelasten = Number(y.interest_paid) || 0;
    const rentebaten = Number(y.interest_income) || 0;
    const netto_rente = rentelasten - rentebaten;
    const resultaat = inkomsten - kosten - afschrijving - netto_rente;
    return { year: String(y.year), inkomsten, kosten, resultaat };
  }), [years]);

  if (!data.length) return null;

  const anyNegative = data.some((d) => d.resultaat < 0);

  return (
    <div className="border-2 border-foreground bg-card p-3" data-testid="ai-pdf-preview-chart">
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <p className="font-display uppercase font-bold text-xs tracking-widest">{t("Snelle sanity-check per boekjaar")}</p>
        <p className="text-[10px] text-muted-foreground italic">
          {t("Verwacht: Inkomsten ≈ Kosten + Afschrijving ± klein resultaat. Sterk afwijkende staven wijzen op een extractie-fout.")}
        </p>
      </div>
      <div style={{ width: "100%", height: 200 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
            <XAxis dataKey="year" tick={{ fontSize: 11, fontFamily: "monospace" }} />
            <YAxis tick={{ fontSize: 11, fontFamily: "monospace" }} tickFormatter={(v) => fmtCurrency(v)} width={72} />
            <Tooltip formatter={(v, name) => [fmtInt(v), name]} labelFormatter={(l) => `${t("Boekjaar")} ${l}`} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="inkomsten" name={t("Inkomsten")} fill="#0047FF" />
            <Bar dataKey="kosten" name={t("Kosten")} fill="#FF5900" />
            <Bar dataKey="resultaat" name={t("Resultaat (excl. rente/afschr.)")} fill="#00D05A" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {anyNegative && (
        <p className="text-[10px] text-destructive mt-1 flex items-center gap-1" data-testid="ai-pdf-preview-chart-warning">
          <AlertTriangle className="w-3 h-3" />
          {t("Eén of meer boekjaren tonen een negatief exploitatieresultaat — controleer of dat klopt met het jaarverslag.")}
        </p>
      )}
    </div>
  );
}


function ReviewPane({ t, years, preview, existingSubposts, patchYear, patchSubpost, patchReserve, onSave, onBack, error }) {
  if (!years.length) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-destructive/10 border-2 border-destructive text-destructive text-sm">
          {t("Geen boekjaren gevonden. Probeer een ander jaarverslag of een duidelijker PDF.")}
        </div>
        <DialogFooter>
          <button onClick={onBack} className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold text-sm">{t("Terug")}</button>
        </DialogFooter>
      </div>
    );
  }

  const totalToImport = years.filter((y) => !(y.already_exists && !y.overwrite)).length;

  return (
    <div className="space-y-4" data-testid="ai-pdf-review">
      {preview?.notes && (
        <div className="p-2 bg-muted/40 border border-border text-xs italic" data-testid="ai-pdf-notes">
          {t("AI-notitie: ")}{preview.notes}
        </div>
      )}
      <ExtractPreviewChart t={t} years={years} />
      {years.map((y, yIdx) => (
        <YearBlock key={y.year} t={t} year={y} yIdx={yIdx}
                   existingSubposts={existingSubposts}
                   preview={preview}
                   patchYear={(patch) => patchYear(yIdx, patch)}
                   patchSubpost={(cat, spIdx, patch) => patchSubpost(yIdx, cat, spIdx, patch)}
                   patchReserve={(rIdx, patch) => patchReserve(yIdx, rIdx, patch)} />
      ))}
      {error && (
        <div className="p-3 bg-destructive/10 border-2 border-destructive text-destructive text-sm" data-testid="ai-pdf-review-error">{error}</div>
      )}
      <DialogFooter className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button onClick={onBack} data-testid="ai-pdf-back-btn"
                  className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> {t("Andere PDF")}
          </button>
          <span className="text-xs text-muted-foreground">{t("{n} boekjaren klaar om te importeren").replace("{n}", totalToImport)}</span>
        </div>
        <button onClick={onSave} disabled={!totalToImport} data-testid="ai-pdf-confirm-btn"
                className={`btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 ${!totalToImport ? "opacity-40 cursor-not-allowed" : ""}`}>
          <Check className="w-4 h-4" /> {t("Bevestig & importeer")}
        </button>
      </DialogFooter>
    </div>
  );
}

function YearBlock({ t, year, yIdx, existingSubposts, preview, patchYear, patchSubpost, patchReserve }) {
  const willSkip = year.already_exists && !year.overwrite;
  return (
    <div className={`border-2 border-foreground bg-card ${willSkip ? "opacity-60" : ""}`} data-testid={`ai-pdf-year-block-${year.year}`}>
      <div className="p-3 border-b-2 border-foreground bg-foreground text-background flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="font-display font-black text-lg uppercase">{t("Boekjaar")} {year.year}</span>
          {year.already_exists && (
            <span className="text-xs bg-destructive/80 text-background px-2 py-0.5 uppercase font-bold" data-testid={`ai-pdf-year-exists-${year.year}`}>
              {t("Bestaat al")}
            </span>
          )}
          <span className="text-[11px] opacity-80" data-testid={`ai-pdf-year-kasstroom-${year.year}`}>
            {t(KASSTROOM_SOURCE_LABEL[year.kasstroom_bron] || KASSTROOM_SOURCE_LABEL.geen)}
          </span>
        </div>
        {year.already_exists && (
          <label className="text-xs uppercase font-bold flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={year.overwrite} onChange={(e) => patchYear({ overwrite: e.target.checked })}
                   data-testid={`ai-pdf-year-overwrite-${year.year}`} />
            {t("Overschrijven")}
          </label>
        )}
      </div>
      <div className="p-3 grid grid-cols-2 md:grid-cols-6 gap-3 text-sm border-b border-border">
        <FieldNum label={t("Inkomsten")}      value={year.revenue}           onChange={(v) => patchYear({ revenue: v })}          testid={`ai-pdf-y-${year.year}-revenue`} />
        <FieldNum label={t("Kosten")}         value={year.operating_costs}   onChange={(v) => patchYear({ operating_costs: v })}  testid={`ai-pdf-y-${year.year}-opex`} />
        <FieldNum label={t("Afschrijving")}   value={year.depreciation}      onChange={(v) => patchYear({ depreciation: v })}     testid={`ai-pdf-y-${year.year}-depreciation`} />
        <FieldNum label={t("Rentelasten")}    value={year.interest_paid}     onChange={(v) => patchYear({ interest_paid: v })}    testid={`ai-pdf-y-${year.year}-interest`} />
        <FieldNum label={t("Rentebaten")}     value={year.interest_income}   onChange={(v) => patchYear({ interest_income: v })}  testid={`ai-pdf-y-${year.year}-interest-income`} />
        <FieldNum label={t("Kas + Bank 31/12")} value={year.cash_bank_balance} onChange={(v) => patchYear({ cash_bank_balance: v })} testid={`ai-pdf-y-${year.year}-cash`} />
      </div>
      <div className="grid md:grid-cols-2 gap-0 md:divide-x-2 divide-y-2 md:divide-y-0 divide-foreground">
        <SubpostSection t={t} title={t("Subposten inkomsten")} items={year.revenue_subposts}
                        existing={existingSubposts.revenue}
                        onPatch={(spIdx, patch) => patchSubpost("revenue", spIdx, patch)}
                        yearId={year.year} cat="revenue" />
        <SubpostSection t={t} title={t("Subposten kosten")} items={year.opex_subposts}
                        existing={existingSubposts.opex}
                        onPatch={(spIdx, patch) => patchSubpost("opex", spIdx, patch)}
                        yearId={year.year} cat="opex" />
      </div>
      <ReservesSection t={t} items={year.reserves || []}
                       existing={preview?.existing_reserves || []}
                       onPatch={(rIdx, patch) => patchReserve(rIdx, patch)}
                       yearId={year.year} />
    </div>
  );
}


function ReservesSection({ t, items, existing, onPatch, yearId }) {
  // Renders per-year reserve movements extracted by the AI. Each row can be
  // routed to an existing reserve (dropdown), auto-created as a new
  // earmarked reserve, or skipped entirely.
  if (!items.length) {
    return (
      <div className="p-3 border-t-2 border-foreground bg-accent/10">
        <p className="font-display uppercase font-bold text-xs tracking-widest text-muted-foreground mb-1">{t("Reserves en voorzieningen")}</p>
        <p className="text-xs italic text-muted-foreground">{t("Geen reserves of voorzieningen in dit boekjaar gevonden.")}</p>
      </div>
    );
  }
  return (
    <div className="p-3 border-t-2 border-foreground bg-accent/10" data-testid={`ai-pdf-y-${yearId}-reserves`}>
      <p className="font-display uppercase font-bold text-xs tracking-widest mb-2">{t("Reserves en voorzieningen")} ({items.length})</p>
      <div className="grid gap-2">
        {items.map((rm, idx) => (
          <div key={`${rm.name || "reserve"}-${idx}`} className="grid grid-cols-12 gap-2 items-center bg-card border border-border p-2">
            <div className="col-span-3">
              <div className="font-mono-num text-xs font-bold truncate" title={rm.name}>{rm.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {rm.category === "general" ? t("Algemeen") : t("Bestemming")}
              </div>
            </div>
            <div className="col-span-3 grid grid-cols-3 gap-1 text-[10px] text-right">
              <span className="text-muted-foreground">{Number(rm.opening_balance || 0).toLocaleString("nl-NL")}</span>
              <span className="text-positive">+{Number(rm.dotatie || 0).toLocaleString("nl-NL")}</span>
              <span className="text-destructive">−{Number(rm.onttrekking || 0).toLocaleString("nl-NL")}</span>
            </div>
            <div className="col-span-2 text-right font-mono-num text-xs font-bold">
              = {Number(rm.closing_balance || 0).toLocaleString("nl-NL")}
            </div>
            <div className="col-span-4">
              <select value={rm.action} onChange={(e) => onPatch(idx, { action: e.target.value })}
                      data-testid={`ai-pdf-reserve-action-${yearId}-${idx}`}
                      className="w-full text-xs border border-foreground bg-background px-2 py-1">
                <option value="new">{t("Nieuw aanmaken")}</option>
                <option value="existing" disabled={!existing.length}>{t("Aan bestaande koppelen")}</option>
                <option value="skip">{t("Overslaan")}</option>
              </select>
              {rm.action === "existing" && existing.length > 0 && (
                <select value={rm.target_name || ""} onChange={(e) => onPatch(idx, { target_name: e.target.value })}
                        data-testid={`ai-pdf-reserve-target-${yearId}-${idx}`}
                        className="w-full mt-1 text-xs border border-border bg-background px-2 py-1">
                  {existing.map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}
                </select>
              )}
              {rm.action === "new" && (
                <input value={rm.target_name || rm.name || ""}
                       onChange={(e) => onPatch(idx, { target_name: e.target.value })}
                       data-testid={`ai-pdf-reserve-newname-${yearId}-${idx}`}
                       className="w-full mt-1 text-xs border border-border bg-background px-2 py-1" />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FieldNum({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] font-display uppercase tracking-widest text-muted-foreground mb-1">{label}</label>
      <NumberCell value={value} onChange={onChange} testid={testid} />
    </div>
  );
}

function SubpostSection({ t, title, items, existing, onPatch, yearId, cat }) {
  if (!items.length) {
    return (
      <div className="p-3">
        <p className="font-display uppercase font-bold text-xs tracking-widest text-muted-foreground mb-2">{title}</p>
        <p className="text-xs italic text-muted-foreground">{t("Geen subposten gevonden.")}</p>
      </div>
    );
  }
  return (
    <div className="p-3">
      <p className="font-display uppercase font-bold text-xs tracking-widest text-muted-foreground mb-2">{title}</p>
      <div className="space-y-1.5">
        {items.map((sp, spIdx) => (
          <div key={`${sp.original_name || "sp"}-${spIdx}`} className="grid grid-cols-12 items-center gap-1.5 text-xs" data-testid={`ai-pdf-sp-${yearId}-${cat}-${spIdx}`}>
            <span className="col-span-4 truncate font-mono-num" title={sp.original_name}>{sp.original_name}</span>
            <select value={sp.action}
                    onChange={(e) => {
                      const action = e.target.value;
                      const target_name = action === "existing" ? (existing[0] || sp.original_name) : sp.original_name;
                      onPatch(spIdx, { action, target_name });
                    }}
                    data-testid={`ai-pdf-sp-${yearId}-${cat}-${spIdx}-action`}
                    className="col-span-3 border border-input bg-background px-1.5 py-1">
              <option value="new">{t("Nieuwe subpost")}</option>
              <option value="existing" disabled={!existing.length}>{t("Bestaande subpost")}</option>
              <option value="skip">{t("Overslaan")}</option>
            </select>
            {sp.action === "existing" ? (
              <select value={sp.target_name}
                      onChange={(e) => onPatch(spIdx, { target_name: e.target.value })}
                      data-testid={`ai-pdf-sp-${yearId}-${cat}-${spIdx}-target`}
                      className="col-span-3 border border-input bg-background px-1.5 py-1 truncate">
                {existing.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            ) : sp.action === "new" ? (
              <input value={sp.target_name}
                     onChange={(e) => onPatch(spIdx, { target_name: e.target.value })}
                     data-testid={`ai-pdf-sp-${yearId}-${cat}-${spIdx}-newname`}
                     className="col-span-3 border border-input bg-background px-1.5 py-1" />
            ) : (
              <span className="col-span-3 text-muted-foreground italic">— {t("overgeslagen")} —</span>
            )}
            <div className="col-span-2 text-right font-mono-num">
              {sp.action === "skip" ? <span className="opacity-40">—</span> : fmtInt(sp.amount)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
